import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Image,
  Input,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';
import {
  operatorProfileApi,
  PARAFA_CODE_MAX_LENGTH,
  SIGNATURE_ACCEPTED_MIME_TYPES,
  SIGNATURE_MAX_BYTES,
} from '../../api/operatorProfileApi';

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

const OperatorProfilePage = () => {
  const user = useAuthStore((state) => state.user);

  const [parafaCode, setParafaCode] = useState('');
  const [savedParafaCode, setSavedParafaCode] = useState('');
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const roleLabel = user?.role?.displayName || user?.role?.name || '—';

  /** Object URL-ul vechi e revocat înainte de a-l înlocui; restul la unmount. */
  const applySignatureBlob = useCallback((blob: Blob | null) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (blob) {
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setSignatureUrl(url);
      setHasSignature(true);
    } else {
      setSignatureUrl(null);
      setHasSignature(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const loadSignature = useCallback(async () => {
    try {
      const blob = await operatorProfileApi.getSignature();
      applySignatureBlob(blob);
    } catch (error) {
      applySignatureBlob(null);
      if (statusOf(error) !== 404) {
        message.error('Semnătura nu a putut fi încărcată.');
      }
    }
  }, [applySignatureBlob]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      const [profileResult, signatureResult] = await Promise.allSettled([
        operatorProfileApi.getProfile(),
        operatorProfileApi.getSignature(),
      ]);
      if (!active) return;

      if (profileResult.status === 'fulfilled') {
        const code = profileResult.value?.parafaCode ?? '';
        setParafaCode(code);
        setSavedParafaCode(code);
      }

      if (signatureResult.status === 'fulfilled') {
        applySignatureBlob(signatureResult.value);
      } else {
        applySignatureBlob(null);
        if (statusOf(signatureResult.reason) !== 404) {
          message.error('Semnătura nu a putut fi încărcată.');
        }
      }

      setLoading(false);
    };

    void load();
    return () => {
      active = false;
    };
  }, [applySignatureBlob]);

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (!SIGNATURE_ACCEPTED_MIME_TYPES.includes(file.type)) {
      message.error('Semnătura trebuie să fie PNG sau JPEG.');
      return Upload.LIST_IGNORE;
    }
    if (file.size > SIGNATURE_MAX_BYTES) {
      message.error('Semnătura depășește limita de 2 MB.');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    setUploading(true);
    try {
      const result = await operatorProfileApi.uploadSignature(file as File);
      message.success('Semnătura a fost încărcată.');
      onSuccess?.(result);
      await loadSignature();
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveParafa = async () => {
    const code = parafaCode.trim();
    if (code.length > PARAFA_CODE_MAX_LENGTH) {
      message.error(`Codul de parafă poate avea cel mult ${PARAFA_CODE_MAX_LENGTH} de caractere.`);
      return;
    }
    setSaving(true);
    try {
      // Răspunsul PATCH e utilizatorul sanitizat (cu parafCode) → refresh local.
      const updated = await operatorProfileApi.updateParafaCode(code);
      const nextCode = updated?.parafaCode ?? code;
      setSavedParafaCode(nextCode);
      setParafaCode(nextCode);
      message.success('Codul de parafă a fost salvat.');
    } catch {
      // interceptorul din api/axios afișează deja eroarea.
    } finally {
      setSaving(false);
    }
  };

  const fallbackText = `Fără semnătură, documentele vor afișa «Dr. ${
    fullName || 'Nume operator'
  } — parafă ${savedParafaCode || 'neconfigurat'}».`;

  return (
    <div>
      <Typography.Title level={2} style={{ marginBottom: 16 }}>
        Profil operator
      </Typography.Title>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="Date operator" size="small">
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Nume complet">{fullName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email">{user?.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Rol">{roleLabel}</Descriptions.Item>
            <Descriptions.Item label="Cod de parafă salvat">
              {savedParafaCode || 'Neconfigurat'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Semnătură" size="small">
          <Spin spinning={loading}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {hasSignature && signatureUrl ? (
                <Image src={signatureUrl} alt="Semnătura operatorului" width={320} />
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="Fără semnătură încărcată"
                  description={fallbackText}
                />
              )}
              <div>
                <Upload
                  accept="image/png,image/jpeg"
                  beforeUpload={beforeUpload}
                  customRequest={customRequest}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    Încarcă semnătura
                  </Button>
                </Upload>
              </div>
            </Space>
          </Spin>
        </Card>

        <Card title="Cod de parafă" size="small">
          <Space wrap>
            <Input
              id="parafa-code"
              aria-label="Cod de parafă"
              placeholder="ex. 1234"
              maxLength={PARAFA_CODE_MAX_LENGTH}
              value={parafaCode}
              onChange={(event) => setParafaCode(event.target.value)}
              style={{ width: 240 }}
            />
            <Button type="primary" loading={saving} onClick={handleSaveParafa}>
              Salvează
            </Button>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default OperatorProfilePage;
